import React, { useRef, useEffect, useContext, useState } from 'react';
import _ from 'lodash';

import MapContext from '../../hooks/MapContext';
import PlasticMap from '../../graphics/D3Map';
import { REG_COLOR_DICT } from '../../styles/color';
import styled from 'styled-components';
import D3Timeline from '../../graphics/D3Timeline';
import { device } from '../../styles/size';

const LEGEND_TEXT = {
  'การห้ามผลิต แจก ใช้ และ/หรือ ซื้อขายอย่างเด็ดขาดหรือบางส่วน':
    'การแบนถุงพลาสติกอย่างเด็ดขาดและบางส่วน (Total or partial ban)',
  เครื่องมือทางเศรษฐศาสตร์:
    'การจัดเก็บค่าธรรมเนียม ค่าปรับ และ/หรือภาษีถุงพลาสติก(Economic instruments)',
  การผสมกันระหว่างเครื่องมือทางเศรษฐศาสตร์และการห้าม:
    'การห้ามใช้และเก็บเงิน (Combination)',
  ข้อตกลงระหว่างภาครัฐและภาคเอกชน:
    'ความร่วมมือระหว่างรัฐและเอกชน (Private public agreement)',
};

const Graphic = styled.figure`
  position: relative;
  @media ${device.tablet} {
    box-shadow: 0 10px 10px rgba(0, 0, 0, 0.375);
  }

  & .marching-ant {
    stroke-dasharray: 3 3;
    stroke-dashoffset: 6;
    animation: moving-dotted 0.6s linear infinite;
  }

  @keyframes moving-dotted {
    to {
      stroke-dashoffset: 0;
    }
  }
`;

const Figcap = styled.figcaption`
  display: flex;
  justify-content: center;
  align-content: center;
  align-items: center;
  font-size: 10px;
  flex-flow: column nowrap;
  position: absolute;
  bottom: 10px;
  left: 0;
  width: 100%;
  height: 64px;
`;

const LegendContainer = styled.div`
  display: flex;
  flex-flow: row wrap;
  justify-content: center;
`;

const Legend = styled.div`
  display: flex;
  align-items: center;

  ::before {
    content: '';
    border-radius: 100%;
    height: 16px;
    width: 16px;
    background-color: ${(props) => props.color};
    margin: 0 5px;
  }
`;

const Tooltip = styled.div`
  position: absolute;
`;

let pm, tl;
const Map = ({ className }) => {
  const { geojson, cpw, step, year, setYear } = useContext(MapContext);
  const mapRef = useRef(null);
  const timelineRef = useRef(null);
  const lineRef = useRef(null);

  const [tooltipContent, setTooltipContent] = useState([]);
  const [tooltipStyles, setTooltipStyles] = useState({
    width: 0,
    left: null,
    top: null,
    opacity: 0,
  });

  useEffect(() => {
    if ([mapRef, lineRef, timelineRef].includes(null) || geojson.length === 0)
      return;
    pm = PlasticMap(geojson, mapRef, setYear, setTooltipContent);
    tl = D3Timeline(cpw, lineRef, timelineRef, setYear);
    window.addEventListener(
      'resize',
      _.throttle(() => {
        pm.resize();
        tl.resize();
      }, 100)
    );
  }, [geojson, mapRef, lineRef, timelineRef]);

  useEffect(() => {
    if (mapRef === null || geojson.length === 0 || step === null) return;
    tl.handleYearChange(year);
  }, [year]);

  useEffect(() => {
    if (mapRef === null || geojson.length === 0 || step === null) return;
    tl.handleStepChange(step);
    pm.handleStepChange(step);
  }, [step]);

  return (
    <Graphic className={className}>
      <Tooltip style={tooltipStyles}>
        {tooltipContent?.properties?.name}
      </Tooltip>
      <svg width="100%" height="100%">
        <g className="line" ref={lineRef}>
          <path className="cpw" />
          <path className="cpw-projection" />
          <g className="axis">
            <g className="y"></g>
          </g>
        </g>
        <g className="container" ref={mapRef}>
          <g
            className="map"
            onMouseMove={(e) => {
              setTooltipStyles({
                top: e.clientY - 50,
                left: e.clientX,
                transform: 'translate(-50%, -50%)',
                opacity: 1,
              });
            }}
            onMouseLeave={() => {
              setTooltipStyles({
                width: 0,
                top: null,
                left: null,
                opacity: 0,
              });
            }}
          ></g>
          <g className="bar-axis">
            <g className="x"></g>
            <g className="y"></g>
          </g>
        </g>
        <g className="timeline" ref={timelineRef}>
          <clipPath id="clipCircle">
            <circle />
          </clipPath>
          <line className="img" />
          <image
            xlinkHref="/wp-content/ippd-content/plasticstory/img/stengustafthulin.jpg"
            clipPath="url(#clipCircle)"
          />
          <circle className="pulse" r="7.5px" fill="green">
            <animate
              attributeType="SVG"
              attributeName="r"
              begin="0s"
              dur="1.5s"
              repeatCount="indefinite"
              from="0%"
              to="5%"
            />
            <animate
              attributeType="CSS"
              attributeName="stroke-width"
              begin="0s"
              dur="1.5s"
              repeatCount="indefinite"
              from="3%"
              to="0%"
            />
            <animate
              attributeType="CSS"
              attributeName="opacity"
              begin="0s"
              dur="1.5s"
              repeatCount="indefinite"
              from="1"
              to="0"
            />
          </circle>
          <g className="axis"></g>
        </g>
      </svg>
      <Figcap
        style={{
          opacity: [13, 14].includes(step?.index) ? 1 : 0,
        }}
      >
        <LegendContainer
          style={{
            opacity: step?.index === 14 ? 1 : 0,
          }}
        >
          <Legend color="rgba(150, 150, 150, .5)">
            (สีอ่อน) ระดับภูมิภาคหรือเมือง
          </Legend>
          <Legend color="rgba(150, 150, 150, 1)">(สีเข้ม) ระดับประเทศ</Legend>
        </LegendContainer>
        <LegendContainer>
          {Object.entries(REG_COLOR_DICT).map(([action, color]) => (
            <Legend key={color} color={color}>
              {LEGEND_TEXT[action]}
            </Legend>
          ))}
        </LegendContainer>
      </Figcap>
    </Graphic>
  );
};

export default Map;
